import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Breadcrumb from "../Breadcrumb";
import FicheHeader from "./FicheHeader";
import { api, formatEUR, formatEURDecimal, formatDateFR, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Loader2, ShieldCheck, CheckCircle2, Send, PenLine, AlertTriangle, Printer } from "lucide-react";

const CONTRACT_STATUS = {
  draft: { label: "Brouillon", cls: "bg-slate-100 text-slate-700", dot: "bg-slate-400" },
  sent: { label: "Envoyé", cls: "bg-blue-100 text-blue-700", dot: "bg-blue-500" },
  signature_requested: { label: "Signature demandée", cls: "bg-amber-100 text-amber-700", dot: "bg-amber-500" },
  signed: { label: "Signé", cls: "bg-emerald-100 text-emerald-700", dot: "bg-emerald-500" },
};

export default function ContractFiche({ bookingId, onBack }) {
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [body, setBody] = useState("");
  const [acting, setActing] = useState(null); // 'send' | 'sign' | 'save'

  useEffect(() => {
    setLoading(true);
    api.get(`/contracts/${bookingId}`)
      .then(({ data }) => setContract(data.contract))
      .catch(() => setContract(null))
      .finally(() => setLoading(false));
  }, [bookingId]);

  const sendContract = async () => {
    setActing("send");
    try {
      const { data } = await api.post(`/contracts/${bookingId}/send`);
      setContract(data.contract);
      toast.success("Contrat envoyé (simulation)");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erreur");
    } finally {
      setActing(null);
    }
  };

  const requestSignature = async () => {
    setActing("sign");
    try {
      const { data } = await api.post(`/contracts/${bookingId}/request-signature`);
      setContract(data.contract);
      toast.success("Signature demandée");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erreur");
    } finally {
      setActing(null);
    }
  };

  const saveBody = async () => {
    setActing("save");
    try {
      const { data } = await api.patch(`/contracts/${bookingId}`, { custom_body: body.trim() || null });
      setContract(data.contract);
      setEditing(false);
      toast.success("Contrat mis à jour");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erreur");
    } finally {
      setActing(null);
    }
  };

  if (loading) {
    return <div className="py-32 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-violet-600" /></div>;
  }
  if (!contract) {
    return (
      <div className="w-full p-10 text-center">
        <h2 className="font-heading text-xl">Contrat introuvable</h2>
        <button onClick={onBack} className="mt-4 inline-flex text-violet-600 hover:underline" data-testid="contract-fiche-back-empty">Retour</button>
      </div>
    );
  }

  const status = contract.vendor_status || "draft";
  const meta = CONTRACT_STATUS[status];
  const isSigned = status === "signed";

  return (
    <div className="w-full p-4 sm:p-6 lg:p-8" data-testid="contract-fiche">
      <Breadcrumb
        items={[
          { label: "Pilotage", href: "/vendor/pilotage" },
          { label: "Réservations", href: "/vendor/pilotage?tab=bookings" },
          { label: "Fiche réservation", href: `/vendor/pilotage?tab=bookings&booking=${bookingId}` },
          { label: "Fiche contrat" },
        ]}
        className="mb-5 flex-wrap"
      />

      <FicheHeader
        onBack={onBack}
        backLabel="Retour à la réservation"
        title={`Contrat de location${contract.reference ? ` ${contract.reference}` : ""}`}
        subtitle={`${contract.vehicle_brand} ${contract.vehicle_model} · ${contract.client_name}`}
        badge={
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${meta.cls}`} data-testid="contract-fiche-status">
            <span className={`h-1.5 w-1.5 rounded-full ${meta.dot}`} /> {meta.label}
          </span>
        }
        editing={editing}
        onEdit={isSigned ? undefined : () => { setBody(contract.custom_body || ""); setEditing(true); }}
        onCancel={() => setEditing(false)}
        onSave={saveBody}
        saving={acting === "save"}
        actions={!editing && (
          <>
            <button
              onClick={() => window.print()}
              className="h-10 px-4 rounded-xl border border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors inline-flex items-center gap-2"
              data-testid="contract-fiche-print-btn"
            >
              <Printer className="h-3.5 w-3.5" /> Imprimer
            </button>
            <button
              onClick={sendContract}
              disabled={isSigned || acting === "send"}
              className="h-10 px-4 rounded-xl border border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors inline-flex items-center gap-2"
              data-testid="contract-fiche-send-btn"
            >
              {acting === "send" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Envoyer
            </button>
            <button
              onClick={requestSignature}
              disabled={isSigned || status === "signature_requested" || acting === "sign"}
              className="h-10 px-4 rounded-xl bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors inline-flex items-center gap-2"
              data-testid="contract-fiche-sign-btn"
            >
              {acting === "sign" ? <Loader2 className="h-4 w-4 animate-spin" /> : <PenLine className="h-3.5 w-3.5" />} Demander signature
            </button>
          </>
        )}
        testid="contract-fiche-header"
      />

      <div className="flex flex-wrap items-center gap-2 mt-3 text-xs text-slate-500">
        {contract.sent_at && <span>Envoyé le {formatDateFR(contract.sent_at.substring(0, 10))}</span>}
        {contract.signature_requested_at && <span>· Signature demandée le {formatDateFR(contract.signature_requested_at.substring(0, 10))}</span>}
      </div>

      <div id="contract-print-area" className="mt-6 max-w-[850px] bg-white rounded-2xl border border-slate-200 p-6 sm:p-8 space-y-8" data-testid="contract-fiche-body">
        {(contract.reference || contract.booking_reference) && (
          <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-slate-500 font-mono">
            {contract.reference && <span data-testid="contract-reference">Contrat {contract.reference}</span>}
            {contract.booking_reference && <span data-testid="contract-booking-reference">Réservation {contract.booking_reference}</span>}
          </div>
        )}
        <Section title="Parties">
          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <div className="text-xs tracking-[0.2em] uppercase font-bold text-slate-400 mb-2">Loueur</div>
              <div className="flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-violet-600" />
                <span className="text-sm font-medium text-slate-900">Loueur certifié Junago</span>
              </div>
              <div className="text-sm text-slate-600 mt-1">{contract.vendor_company}</div>
              <div className="text-sm text-slate-500">{contract.vendor_city}</div>
            </div>
            <div>
              <div className="text-xs tracking-[0.2em] uppercase font-bold text-slate-400 mb-2">Locataire</div>
              <div className="text-sm font-medium text-slate-900">{contract.client_name}</div>
              {contract.main_driver?.license_number && (
                <div className="text-xs text-slate-500 mt-0.5">Permis n° {contract.main_driver.license_number}</div>
              )}
              {(contract.additional_drivers || []).length > 0 && (
                <div className="mt-2" data-testid="contract-additional-drivers">
                  <div className="text-[10px] tracking-[0.15em] uppercase font-bold text-slate-400">Conducteur{contract.additional_drivers.length > 1 ? "s" : ""} supplémentaire{contract.additional_drivers.length > 1 ? "s" : ""}</div>
                  {contract.additional_drivers.map((d, i) => (
                    <div key={i} className="text-sm text-slate-700 mt-0.5">
                      {d.name}{d.license_number ? <span className="text-xs text-slate-500"> · Permis n° {d.license_number}</span> : null}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </Section>

        <Section title="Véhicule">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <Row label="Marque / Modèle" value={`${contract.vehicle_brand} ${contract.vehicle_model}`} />
            <Row label="Catégorie" value={contract.vehicle_category} />
            <Row label="Immatriculation" value={contract.vehicle_plate || "—"} />
            <Row label="Lieu" value={contract.vehicle_city} />
          </div>
        </Section>

        <Section title="Location">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <Row label="Début" value={`${formatDateFR(contract.start_date)}${contract.start_time ? ` à ${contract.start_time}` : ""}`} />
            <Row label="Fin" value={`${formatDateFR(contract.end_date)}${contract.end_time ? ` à ${contract.end_time}` : ""}`} />
            <Row label="Durée" value={`${contract.days} jour${contract.days > 1 ? "s" : ""}`} />
            {contract.pickup_location_name && <Row label="Agence de départ" value={contract.pickup_location_name} />}
            {contract.return_location_name && <Row label="Agence de retour" value={contract.return_location_name} />}
          </div>
        </Section>

        <Section title="Tarif">
          <div className="grid grid-cols-2 gap-4 text-sm" data-testid="contract-pricing">
            <Row label="Tarif journalier" value={formatEUR(contract.price_per_day)} />
            {contract.subtotal != null && <Row label="Sous-total" value={formatEUR(contract.subtotal)} />}
            {(contract.options || []).map((o) => <Row key={o.key} label={o.label} value={`+ ${formatEUR(o.amount)}`} />)}
            {(contract.discount_amount || 0) > 0 && <Row label="Remise" value={`− ${formatEUR(contract.discount_amount)}`} />}
            {(contract.vat_amount || 0) > 0 && <Row label={`TVA (${contract.vat_rate}%)`} value={formatEUR(contract.vat_amount)} />}
            <Row label="Total" value={formatEUR(contract.total)} bold />
            {contract.legal_snapshot?.show_deposit !== false && contract.caution_amount ? (
              <Row label="Caution (empreinte)" value={formatEUR(contract.caution_amount)} />
            ) : null}
            {contract.legal_snapshot?.franchise_amount ? (
              <Row label="Franchise en cas de dommages" value={formatEUR(contract.legal_snapshot.franchise_amount)} />
            ) : null}
            {contract.legal_snapshot?.included_km_per_day ? (
              <Row label="Kilométrage inclus" value={`${contract.legal_snapshot.included_km_per_day} km/jour`} />
            ) : null}
            {contract.legal_snapshot?.extra_km_price ? (
              <Row label="Km supplémentaire" value={`${formatEURDecimal(contract.legal_snapshot.extra_km_price)}/km`} />
            ) : null}
          </div>
        </Section>

        <Section title={editing || contract.custom_body ? "Conditions personnalisées" : "Conditions"}>
          {editing ? (
            <div>
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={12}
                placeholder="Ex : Clause spécifique pour ce client, conditions particulières, franchise différente… Laissez vide pour utiliser les conditions par défaut."
                className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm font-mono leading-relaxed focus:ring-2 focus:ring-violet-600 focus:border-violet-600 outline-none"
                data-testid="contract-fiche-body-textarea"
              />
              <div className="text-[10px] text-slate-400 mt-1.5">Laissez vide pour utiliser les conditions par défaut.</div>
            </div>
          ) : contract.custom_body ? (
            <div className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed" data-testid="contract-fiche-custom-body">
              {contract.custom_body}
            </div>
          ) : contract.legal_snapshot?.contract_text ? (
            <div className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed" data-testid="contract-fiche-legal-text">
              {contract.legal_snapshot.contract_text}
            </div>
          ) : (
            <ul className="space-y-2.5">
              {(contract.conditions || []).map((c, idx) => (
                <li key={`condition-${idx}`} className="flex items-start gap-2 text-sm text-slate-600">
                  <CheckCircle2 className="h-4 w-4 text-violet-500 mt-0.5 shrink-0" />
                  {c}
                </li>
              ))}
            </ul>
          )}
        </Section>

        {contract.legal_snapshot?.cgv && (
          <Section title="Conditions générales de location">
            <div className="text-xs text-slate-600 whitespace-pre-wrap leading-relaxed" data-testid="contract-fiche-cgv">{contract.legal_snapshot.cgv}</div>
          </Section>
        )}

        {contract.legal_snapshot?.mentions && (
          <div className="text-[10px] text-slate-400 whitespace-pre-wrap leading-relaxed border-t border-slate-100 pt-4" data-testid="contract-fiche-mentions">{contract.legal_snapshot.mentions}</div>
        )}

        <Section title="Signatures">
          <div className="grid sm:grid-cols-2 gap-6">
            <SigBlock label="Loueur" sig={contract.vendor_signature} stamp={contract.vendor_stamp} isVendor />
            <SigBlock label="Client" sig={contract.client_signature} />
          </div>

          {!contract.vendor_signature && (
            <div className="mt-4 rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 flex items-start gap-2" data-testid="contract-fiche-no-signature-warning">
              <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
              <div className="text-sm text-slate-700">
                Aucune signature n'est enregistrée sur votre profil. Configurez-la dans <Link to="/vendor/parametrages" className="font-semibold text-violet-700 underline">Paramétrages → Signature</Link>. Elle sera automatiquement apposée sur ce contrat.
              </div>
            </div>
          )}
          {contract.vendor_signature && (
            <div className="mt-4 text-xs text-slate-500">Votre signature a été automatiquement apposée depuis votre profil.</div>
          )}
        </Section>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-xs tracking-[0.2em] uppercase font-bold text-violet-600 mb-3">{title}</div>
      {children}
    </div>
  );
}

function Row({ label, value, bold }) {
  return (
    <div>
      <div className="text-slate-500 text-xs">{label}</div>
      <div className={`mt-0.5 ${bold ? "font-semibold text-slate-900" : "text-slate-900"}`}>{value}</div>
    </div>
  );
}

function SigBlock({ label, sig, stamp, isVendor }) {
  return (
    <div className="rounded-xl border border-slate-200 p-4 bg-slate-50/50" data-testid={`contract-fiche-sigblock-${label.toLowerCase()}`}>
      <div className="text-xs tracking-[0.2em] uppercase font-bold text-slate-400 mb-2">{label}</div>
      {sig ? (
        <>
          <div className="relative inline-flex items-center gap-3">
            <img src={sig.data_base64} alt="Signature" className="h-16 object-contain" />
            {isVendor && stamp && <img src={stamp} alt="Tampon" className="h-16 object-contain opacity-90" />}
          </div>
          <div className="text-[10px] text-slate-400 mt-2">
            {sig.signer_name}
            {sig.auto_applied && <span className="ml-1 text-violet-600 font-semibold">· signature auto-apposée</span>}
            {sig.signed_at && <> · {formatDateFR(sig.signed_at?.split("T")[0])}</>}
          </div>
          {sig.ip_address && <div className="text-[10px] text-slate-400">IP : {sig.ip_address}</div>}
        </>
      ) : (
        <div className="h-16 flex items-center justify-center text-xs text-slate-400">En attente de signature</div>
      )}
    </div>
  );
}
